import React, { Component } from 'react';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import {
  BASE_CURRENCY,
  convertAndClampRange,
  convertConfigValue,
  deriveSliderStep,
  resolveSliderMax,
  selectDisplayCurrency,
} from './priceFilterUtils';

import IconPlus from '../IconPlus/IconPlus';
import PriceFilterForm from '../PriceFilterForm/PriceFilterForm';

import css from './PriceFilterPlain.module.css';

const RADIX = 10;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = priceRange => {
  const [minPrice, maxPrice] = !!priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;
  return { [queryParamName]: value };
};

/**
 * PriceFilterPlainComponent component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The ID
 * @param {React.Node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {string} props.initialValues.minPrice - The price range: min
 * @param {string} props.initialValues.maxPrice - The price range: max
 * @param {number} props.max - Fallback ceiling from config, used only until the
 *   dynamic ceiling is known. The floor is always 0 and the step is derived
 *   from the ceiling, so the configured min/step are not used.
 * @param {number|null} props.maxPriceEUR - Most expensive listing (whole euros)
 *   matching the current non-price filters; drives the dynamic ceiling
 * @param {string} props.marketplaceCurrency - The marketplace currency (e.g. 'USD')
 * @param {Function} props.onSubmit - The function to submit
 * @param {intlShape} props.intl - The intl object
 * @param {string} props.selectedCurrency - Currently selected display currency
 * @param {Object} props.exchangeRates - Exchange rates fetched from API
 * @returns {JSX.Element}
 */
class PriceFilterPlainComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: false };

    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.toggleIsOpen = this.toggleIsOpen.bind(this);
  }

  handleChange(values) {
    const {
      onSubmit,
      queryParamNames,
      marketplaceCurrency,
      selectedCurrency,
      exchangeRates,
    } = this.props;
    const displayCurrency = selectDisplayCurrency(
      selectedCurrency,
      marketplaceCurrency,
      exchangeRates
    );
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    // Only the floor is enforced: the ceiling is dynamic, so clamping to the
    // static config max here would truncate every range above it.
    const baseValues = convertAndClampRange(
      values,
      displayCurrency,
      BASE_CURRENCY,
      exchangeRates,
      0,
      null
    );
    onSubmit(format(baseValues, priceQueryParamName));
  }

  handleClear() {
    const { onSubmit, queryParamNames } = this.props;
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(null, priceQueryParamName));
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  render() {
    const asNumberOr = (value, fallback) =>
      typeof value === 'number' && !Number.isNaN(value) ? value : fallback;

    const {
      rootClassName,
      className,
      id,
      label,
      queryParamNames,
      initialValues,
      max,
      intl,
      marketplaceCurrency,
      selectedCurrency,
      exchangeRates,
      maxPriceEUR,
    } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    const displayCurrency = selectDisplayCurrency(
      selectedCurrency,
      marketplaceCurrency,
      exchangeRates
    );
    // The floor is always 0. The ceiling is the most expensive listing matching
    // the current non-price filters, converted into the display currency and
    // rounded up to the next full thousand; the configured max is only the
    // fallback for when that is not known yet.
    const fallbackMax = asNumberOr(
      convertConfigValue(max, marketplaceCurrency, displayCurrency, exchangeRates),
      max
    );
    const displayMin = 0;
    const displayMax = resolveSliderMax({
      maxPriceEUR,
      displayCurrency,
      exchangeRates,
      fallbackMax,
    });
    const displayStep = deriveSliderStep(displayMax);

    const priceQueryParam = getPriceQueryParamName(queryParamNames);
    const initialPrice = initialValues ? parse(initialValues[priceQueryParam]) : {};
    const displayInitialPrice = convertAndClampRange(
      initialPrice,
      BASE_CURRENCY,
      displayCurrency,
      exchangeRates,
      displayMin,
      displayMax
    );
    const { minPrice, maxPrice } = displayInitialPrice || {};

    const hasValue = value => value != null;
    const hasInitialValues = initialValues && hasValue(minPrice) && hasValue(maxPrice);

    const hasActiveSelection = hasInitialValues;
    return (
      <div className={classes}>
        <div className={css.filterHeader}>
          <button type="button" className={css.labelButton} onClick={this.toggleIsOpen}>
            <span className={css.labelButtonContent}>
              <span className={css.labelWrapper}>
                <span className={css.label}>
                  {label}
                  {hasActiveSelection ? (
                    <span className={css.labelBadge}>1</span>
                  ) : null}
                </span>
              </span>
              <span className={css.openSign}>
                <IconPlus isOpen={this.state.isOpen} isSelected={hasInitialValues} />
              </span>
            </span>
          </button>
        </div>
        <div className={css.formWrapper}>
          <PriceFilterForm
            id={id}
            initialValues={
              hasInitialValues
                ? displayInitialPrice
                : { minPrice: displayMin, maxPrice: displayMax }
            }
            onChange={this.handleChange}
            intl={intl}
            contentRef={node => {
              this.filterContent = node;
            }}
            min={displayMin}
            max={displayMax}
            step={displayStep}
            currencyPrefix={displayCurrency}
            liveEdit
            isOpen={this.state.isOpen}
            isInSideBar
            style={{ minWidth: '160px' }}
          >
            <button type="button" className={css.clearButton} onClick={this.handleClear}>
              <FormattedMessage id={'PriceFilter.clear'} />
            </button>
          </PriceFilterForm>
        </div>
      </div>
    );
  }
}

const PriceFilterPlain = injectIntl(PriceFilterPlainComponent);

export default PriceFilterPlain;
